import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, fileUrl } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Camera, Trash, CheckCircle, Circle, PencilSimpleLine } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import SignaturePad from "@/components/SignaturePad";

const DEFAULT_CHECKLIST = [
  "EPP verificat (echipament protecție)",
  "Zonă de lucru securizată",
  "Scule & echipamente conforme",
  "Materiale recepționate pe șantier",
  "Verificare aliniament / cote",
];

export default function FieldPanel({ projectId }) {
  const [reports, setReports] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ work_done: "", weather: "", progress_pct: 0, hours: 8, client_name: "" });
  const [checklist, setChecklist] = useState(DEFAULT_CHECKLIST.map((label) => ({ label, done: false })));
  const [photos, setPhotos] = useState([]);
  const [sig, setSig] = useState(null);

  const load = () => api.get(`/projects/${projectId}/field-reports`).then((r) => setReports(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/upload", fd);
        setPhotos((p) => [...p, data.path]);
      } catch { toast.error("Încărcare poză eșuată"); }
    }
  };

  const submit = async () => {
    setSaving(true);
    try {
      let sigPath = null;
      if (sig) {
        const { data } = await api.post("/upload-base64", { data: sig, ext: "png" });
        sigPath = data.path;
      }
      await api.post(`/projects/${projectId}/field-reports`, {
        project_id: projectId, work_done: form.work_done, weather: form.weather,
        progress_pct: Number(form.progress_pct) || 0, hours: Number(form.hours) || 0,
        checklist, photos, client_name: form.client_name, client_signature_url: sigPath,
      });
      toast.success("Raport de teren trimis");
      setOpen(false);
      setForm({ work_done: "", weather: "", progress_pct: 0, hours: 8, client_name: "" });
      setChecklist(DEFAULT_CHECKLIST.map((label) => ({ label, done: false })));
      setPhotos([]); setSig(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggle = (i) => setChecklist((c) => c.map((x, idx) => (idx === i ? { ...x, done: !x.done } : x)));

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="field-panel">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-head text-lg font-bold">Rapoarte teren (montaj)</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" data-testid="add-report-btn"><Plus size={15} className="mr-1" /> Raport zilnic</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Raport zilnic de teren</DialogTitle></DialogHeader>
            <div className="grid max-h-[68vh] gap-3 overflow-y-auto pr-1">
              <div className="grid gap-1.5"><Label>Lucrări efectuate</Label><Textarea value={form.work_done} onChange={(e) => setForm({ ...form, work_done: e.target.value })} data-testid="report-work" /></div>
              <div className="grid grid-cols-3 gap-2">
                <div className="grid gap-1.5"><Label>Progres %</Label><Input type="number" value={form.progress_pct} onChange={(e) => setForm({ ...form, progress_pct: e.target.value })} data-testid="report-progress" /></div>
                <div className="grid gap-1.5"><Label>Ore</Label><Input type="number" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} /></div>
                <div className="grid gap-1.5"><Label>Vreme</Label><Input value={form.weather} onChange={(e) => setForm({ ...form, weather: e.target.value })} /></div>
              </div>
              <div>
                <Label className="mb-1.5 block">Checklist</Label>
                <div className="space-y-1">
                  {checklist.map((c, i) => (
                    <button key={i} onClick={() => toggle(i)} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-white/5" data-testid={`check-${i}`}>
                      {c.done ? <CheckCircle size={17} weight="fill" className="text-emerald-400" /> : <Circle size={17} className="text-muted-foreground" />}
                      <span className={c.done ? "text-foreground" : "text-muted-foreground"}>{c.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="mb-1.5 block">Poze șantier</Label>
                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2.5 text-sm text-muted-foreground hover:border-primary/50">
                  <Camera size={18} /> Adaugă poze
                  <input type="file" accept="image/*" multiple className="hidden" onChange={uploadPhotos} data-testid="photo-input" />
                </label>
                {photos.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {photos.map((p, i) => (
                      <div key={i} className="relative">
                        <img src={fileUrl(p)} alt="" className="h-16 w-16 rounded-lg object-cover" />
                        <button onClick={() => setPhotos((ph) => ph.filter((_, idx) => idx !== i))} className="absolute -right-1 -top-1 rounded-full bg-red-500 p-0.5 text-white"><Trash size={11} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid gap-1.5"><Label>Nume client (semnătură)</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="report-client" /></div>
              <div>
                <Label className="mb-1.5 flex items-center gap-1.5"><PencilSimpleLine size={15} /> Semnătură client</Label>
                <SignaturePad onCapture={setSig} />
              </div>
            </div>
            <DialogFooter><Button onClick={submit} disabled={saving} data-testid="submit-report-btn">{saving ? "Se trimite…" : "Trimite raport"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {reports.map((r) => (
          <div key={r.id} className="rounded-lg border border-border p-4" data-testid={`report-${r.id}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">{fmtDate(r.report_date)} · {r.author_name}</span>
              <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-bold text-primary">{r.progress_pct}%</span>
            </div>
            {r.work_done && <p className="mt-1.5 text-sm text-muted-foreground">{r.work_done}</p>}
            <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
              {r.hours ? <span>{r.hours}h lucrate</span> : null}
              {r.weather ? <span>Vreme: {r.weather}</span> : null}
              {r.checklist?.length ? <span>{r.checklist.filter((c) => c.done).length}/{r.checklist.length} checklist</span> : null}
            </div>
            {r.photos?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {r.photos.map((p, i) => <img key={i} src={fileUrl(p)} alt="" className="h-20 w-20 rounded-lg object-cover" />)}
              </div>
            )}
            {r.client_signature_url && (
              <div className="mt-3 inline-block rounded-lg border border-border bg-white p-1">
                <img src={fileUrl(r.client_signature_url)} alt="semnătură" className="h-14" />
                <div className="px-1 pb-0.5 text-[10px] text-neutral-500">Semnat: {r.client_name || "client"}</div>
              </div>
            )}
          </div>
        ))}
        {reports.length === 0 && <p className="text-sm text-muted-foreground">Niciun raport de teren încă.</p>}
      </div>
    </div>
  );
}
